import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLazyQuery } from '@apollo/client';
import dayjs from 'dayjs';
import {
    CHECK_APPOINTMENT_FOR_DATE,
    CheckAppointmentForDateResponse,
} from '../../appointment/graphql/queries/checkExistsAppointmentForDate';

/**
 * 部分揽收原因 → 手动加单原因。
 * 「其他」归到部分补遗：三个原因本质都是「没收完要补」，PRD 只给了前两条的映射。
 */
const PARTIAL_REASON_TO_MANUAL_REASON: Record<number, string> = {
    1: 'PARTIAL_PICKUP',
    2: 'VOLUME_OVERFLOW_SPLIT',
    3: 'PARTIAL_PICKUP',
};

interface ScheduleTarget {
    /** 揽收站点 ID（cooperation_warehouse.id） */
    pickupSiteId?: number;
    /** 站点地址 */
    address?: string;
    /** 原失败预约单渠道 ID，用于复揽建单预填渠道 */
    channelId?: number;
    /** 原疑似虚假预约单 ID，复揽建单时落库(repickupSourceAppointmentId)供精确判定已完结 */
    appointmentId?: number;
    /**
     * 部分揽收原因（仅部分揽收抽屉传）：据此预填手动加单原因。
     * 不传时保持原行为不带 manualReason 参数，建单页用默认值——虚假上门抽屉走这条路径。
     */
    partialPickupReason?: number;
}

interface Props {
    visible: boolean;
    onClose: () => void;
    target: ScheduleTarget | null;
    /** 进度页当前揽收日期（YYYY-MM-DD），用于计算"当日/次日" */
    baseDate: string;
}

type Choice = 'today' | 'nextday';

/**
 * 复揽「安排司机上门」弹窗。
 * 顶部展示站点 ID + 地址（不展示预约单号）；当日 / 次日上门二选一；
 * 确认后跳转预约单页并打开创建（按所选日期）。
 *
 * 说明：站点信息自动预填需预约单创建页支持 site 参数（目前仅支持 confirm + expectedPickupDate），
 * 故本弹窗确认先按日期跳转，站点预填待预约单页补参数后接入。
 */
const SchedulePickupModal: React.FC<Props> = ({ visible, onClose, target, baseDate }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const tq = (k: string) => t(`pickup.progress.qualityFeedback.${k}`);
    const [choice, setChoice] = useState<Choice | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);

    useEffect(() => {
        if (visible) {
            setChoice(null);
            setSubmitting(false);
        }
    }, [visible]);

    const jumpToCreate = (date: string, manualReason?: string) => {
        // 携带 pickupSiteId + channelId：预约单创建页据此自动打开创建抽屉并预填揽收站点、渠道
        const siteParam = target?.pickupSiteId != null ? `&pickupSiteId=${target.pickupSiteId}` : '';
        const channelParam = target?.channelId != null ? `&channelId=${target.channelId}` : '';
        // 原单 ID：复揽建单落库 repickupSourceAppointmentId，供「已安排」判定与二次上门单号反查
        const sourceParam = target?.appointmentId != null ? `&sourceAppointmentId=${target.appointmentId}` : '';
        const reasonParam = manualReason ? `&manualReason=${manualReason}` : '';
        // 日期额外走 location.state 再传一次：预约单页有一个「刷新过页面就把筛选清成默认值」的
        // effect（PickupAppointmentsPage.tsx:152），它判定的是 performance navigation.type，
        // 而该值反映的是**整个文档的加载方式**、在 SPA 里会终身保持 reload——tab 被刷新过一次后，
        // 此后每次跳进预约单页都会把 URL 带来的 expectedPickupDate 冲回「今天」。
        // 症状是选「次日」却建到当日，且当日通常已有原单 → 被 validateUniqueness 判 duplicate 拒掉。
        // 消费方（同文件 openCreate 分支）声明在那个 effect 之后，能覆盖它的结果。
        navigate(
            `/pickup/appointment?confirm=1&expectedPickupDate=${date}${siteParam}${channelParam}${sourceParam}${reasonParam}`,
            {state: {openCreate: true, cooperationWarehouseId: target?.pickupSiteId, expectedPickupDate: date}},
        );
        onClose();
    };

    /**
     * 目标日该站点是否已有未取消预约单 → 决定预填哪个加单原因。
     *
     * 后端 validateUniqueness 的两条规则严格互补：特殊原因(部分补遗/超载分批)当日有单才放行、
     * 无单会被拒；常规上门则相反，当日有单就是 duplicate。所以不能像 PRD 写的那样按当日/次日
     * 固定取值——长期揽收站点次日通常已有单，固定「常规上门」会一单都建不出来。
     */
    const [checkDate] = useLazyQuery<CheckAppointmentForDateResponse>(CHECK_APPOINTMENT_FOR_DATE, {
        fetchPolicy: 'network-only',
    });

    const onConfirm = async () => {
        if (!choice || submitting) return;
        const base = dayjs(baseDate);
        const date = (choice === 'today' ? base : base.add(1, 'day')).format('YYYY-MM-DD');

        // 非部分揽收场景（虚假上门抽屉）保持原行为：不带 manualReason，建单页用默认值
        if (target?.partialPickupReason == null || target?.pickupSiteId == null) {
            jumpToCreate(date);
            return;
        }

        const specialReason = PARTIAL_REASON_TO_MANUAL_REASON[target.partialPickupReason] || 'PARTIAL_PICKUP';
        setSubmitting(true);
        try {
            const res = await checkDate({
                variables: { cooperationWarehouseId: target.pickupSiteId, expectedPickupDate: date },
            });
            const exists = res.data?.checkAppointmentForDate?.exists;
            jumpToCreate(date, exists ? specialReason : 'REGULAR_PICKUP');
        } catch (error) {
            // 查不到就按特殊原因走：当日上门场景原单必然在，特殊原因一定能过校验；
            // 次日若判断错，建单页会给出明确提示让运营改选，比卡在弹窗里强
            console.error('checkAppointmentForDate failed, fallback to special reason:', error);
            jumpToCreate(date, specialReason);
        } finally {
            setSubmitting(false);
        }
    };

    const optionStyle = (active: boolean): React.CSSProperties => ({
        display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px',
        borderRadius: 8, cursor: 'pointer', fontSize: 13.5,
        border: `1.5px solid ${active ? '#6C4CE3' : '#E2E0EC'}`,
        background: active ? '#F1EDFC' : '#fff',
    });
    const dotStyle = (active: boolean): React.CSSProperties => ({
        width: 16, height: 16, borderRadius: '50%', flex: 'none',
        border: `1.5px solid ${active ? '#6C4CE3' : '#C9C6DA'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    });

    return (
        <Modal title={tq('schedule')} open={visible} onCancel={onClose} footer={null} width={400} destroyOnClose>
            <div style={{ fontSize: 12.5, color: '#8A8FA3', margin: '4px 0 18px', lineHeight: 1.6 }}>
                {tq('siteId')} {target?.pickupSiteId ?? '--'}
                <br />
                {target?.address || '--'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 22 }}>
                <div style={optionStyle(choice === 'today')} onClick={() => setChoice('today')}>
                    <span style={dotStyle(choice === 'today')}>
                        {choice === 'today' && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6C4CE3' }} />}
                    </span>
                    {tq('scheduleToday')}
                </div>
                <div style={optionStyle(choice === 'nextday')} onClick={() => setChoice('nextday')}>
                    <span style={dotStyle(choice === 'nextday')}>
                        {choice === 'nextday' && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#6C4CE3' }} />}
                    </span>
                    {tq('scheduleNextDay')}
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <Button onClick={onClose}>{tq('cancel')}</Button>
                <Button type="primary" disabled={!choice} loading={submitting} onClick={onConfirm}>{tq('confirm')}</Button>
            </div>
        </Modal>
    );
};

export default SchedulePickupModal;
